// @ts-nocheck
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  CONSTRAINT_TYPE_LABELS,
  SEVERITY_LABELS,
} from "@/lib/constraints/humanize";

interface TeamOption {
  value: string;
  label: string;
}

interface Props {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  teamsForFilter: TeamOption[];
  opponentOptions: TeamOption[];
}

const ALL_CONSTRAINT_TYPES = [
  "team_blackout",
  "must_play_before",
  "must_play_after",
  "coach_conflict",
  "venue_preference",
  "game_count_limit",
  "divisional_ordering",
  "do_not_play",
  "must_play_same_time",

] as const;

export function AddOperatorConstraintDialog({
  eventId,
  open,
  onOpenChange,
  onSuccess,
  teamsForFilter,
  opponentOptions,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [teamId, setTeamId] = useState<string>("__event_wide__");
  const [constraintType, setConstraintType] = useState("team_blackout");
  const [severity, setSeverity] = useState("hard");
  const [notes, setNotes] = useState("");

  // Parameter fields
  const [formDate, setFormDate] = useState("");
  const [formStartTime, setFormStartTime] = useState("");
  const [formEndTime, setFormEndTime] = useState("");
  const [formTime, setFormTime] = useState("");
  const [formRelatedTeamId, setFormRelatedTeamId] = useState("");
  // venue_preference
  const [formVenueName, setFormVenueName] = useState("");
  // game_count_limit
  const [formMaxGames, setFormMaxGames] = useState(3);
  const [formScope, setFormScope] = useState("per_day");
  // divisional_ordering
  const [formDivisionName, setFormDivisionName] = useState("");
  const [formPriority, setFormPriority] = useState(1);

  const resetForm = () => {
    setTeamId("__event_wide__");
    setConstraintType("team_blackout");
    setSeverity("hard");
    setNotes("");
    setFormDate("");
    setFormStartTime("");
    setFormEndTime("");
    setFormTime("");
    setFormRelatedTeamId("");
    setFormVenueName("");
    setFormMaxGames(3);
    setFormScope("per_day");
    setFormDivisionName("");
    setFormPriority(1);
  };

  const validate = (): string | null => {
    if (constraintType === "team_blackout") {
      if (!formDate) return "Date is required.";
      if (!formStartTime || !formEndTime) return "Both start and end times are required.";
      if (formStartTime >= formEndTime) return "Start time must be before end time.";
    }
    if (constraintType === "must_play_before" || constraintType === "must_play_after") {
      if (!formDate) return "Date is required.";
      if (!formTime) return "Time is required.";
    }
    if (constraintType === "coach_conflict") {
      if (!formRelatedTeamId) return "Select a related team.";
    }
    if (constraintType === "venue_preference") {
      if (!formVenueName.trim()) return "Venue name is required.";
    }
    if (constraintType === "game_count_limit") {
      if (formMaxGames < 1) return "Max games must be at least 1.";
    }
    if (constraintType === "divisional_ordering") {
      if (!formDivisionName.trim()) return "Division name is required.";
    }
    return null;
  };

  const buildParams = (): Record<string, any> => {
    switch (constraintType) {
      case "team_blackout":
        return { date: formDate, start_time: formStartTime, end_time: formEndTime };
      case "must_play_before":
      case "must_play_after":
        return { date: formDate, time: formTime };
      case "coach_conflict":
        return { related_team_id: formRelatedTeamId };
      case "venue_preference":
        // TODO: solver will need real venue IDs; for now store venue_name as free text
        return { venue_name: formVenueName.trim() };
      case "game_count_limit":
        return { max_games: formMaxGames, scope: formScope };
      case "divisional_ordering":
        // TODO: expand when divisional ordering solver is built
        return { division_name: formDivisionName.trim(), priority: formPriority };
      default:
        return {};
    }
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setSaving(true);

    const resolvedTeamId = teamId === "__event_wide__" ? null : teamId;

    const { error } = await supabase.rpc("create_operator_constraint", {
      p_event_id: eventId,
      p_constraint_type: constraintType,
      p_parameters: buildParams(),
      p_team_id: resolvedTeamId,
      p_severity: severity,
      p_response_notes: notes.trim() || null,
    });

    if (error) {
      console.error("Error creating operator constraint:", error);
      toast.error("Failed to create constraint");
      setSaving(false);
      return;
    }

    toast.success("Constraint created");
    setSaving(false);
    resetForm();
    onOpenChange(false);
    onSuccess();
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) resetForm();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Operator Constraint</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Team */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Team</label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__event_wide__">Event-wide</SelectItem>
                {teamsForFilter.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Constraint type */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Constraint type</label>
            <Select value={constraintType} onValueChange={setConstraintType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_CONSTRAINT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {CONSTRAINT_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* team_blackout */}
          {constraintType === "team_blackout" && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Date</label>
                <Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Start time</label>
                  <Input type="time" value={formStartTime} onChange={(e) => setFormStartTime(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">End time</label>
                  <Input type="time" value={formEndTime} onChange={(e) => setFormEndTime(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* must_play_before / must_play_after */}
          {(constraintType === "must_play_before" || constraintType === "must_play_after") && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Date</label>
                <Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Time</label>
                <Input type="time" value={formTime} onChange={(e) => setFormTime(e.target.value)} />
              </div>
            </div>
          )}

          {/* coach_conflict */}
          {constraintType === "coach_conflict" && (
            <div className="space-y-1">
              <label className="text-sm font-medium">Related team</label>
              <Select value={formRelatedTeamId} onValueChange={setFormRelatedTeamId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a team..." />
                </SelectTrigger>
                <SelectContent>
                  {opponentOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                These two teams cannot play at the same time
              </p>
            </div>
          )}

          {/* venue_preference */}
          {constraintType === "venue_preference" && (
            <div className="space-y-1">
              {/* TODO: solver will need real venue IDs; for now store venue_name as free text */}
              <label className="text-sm font-medium">Venue name</label>
              <Input
                value={formVenueName}
                onChange={(e) => setFormVenueName(e.target.value)}
                placeholder="e.g., Field A"
              />
            </div>
          )}

          {/* game_count_limit */}
          {constraintType === "game_count_limit" && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Max games</label>
                <Input
                  type="number"
                  min={1}
                  value={formMaxGames}
                  onChange={(e) => setFormMaxGames(Number(e.target.value))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Scope</label>
                <Select value={formScope} onValueChange={setFormScope}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_day">Per day</SelectItem>
                    <SelectItem value="per_event">Per event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* divisional_ordering */}
          {constraintType === "divisional_ordering" && (
            <div className="space-y-3">
              {/* TODO: expand when divisional ordering solver is built */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Division name</label>
                <Input
                  value={formDivisionName}
                  onChange={(e) => setFormDivisionName(e.target.value)}
                  placeholder="e.g., U14 Gold"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Priority</label>
                <Input
                  type="number"
                  min={1}
                  value={formPriority}
                  onChange={(e) => setFormPriority(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Lower number = higher priority
                </p>
              </div>
            </div>
          )}

          {/* Severity */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Severity</label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SEVERITY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes about this constraint..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create constraint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}